import { Button, Divider, Stack, Typography } from '@mui/material';
import { PlanWrapper } from './styles';
import PlanList from './PlanList';
import { FC, useEffect, useState, useMemo } from 'react';
import { PricingTier, PricingData, LifetimeCapLimits } from '@/services/pricingService';
import { Subscription } from '@/services/noteGoApi/noteGoApiSchemas';
import { isFreePlan, initializePricingCache } from '@/services/subscriptionService';

/**
 * Props for the PlanCard component.
 *
 * @property plan - The pricing tier information for the plan being displayed.
 * @property selectedFrequency - The currently selected billing frequency (e.g., 'monthly', 'yearly').
 * @property currentUserPlan - The current subscription of the user, if any.
 * @property pricingData - The pricing data for all available plans.
 * @property handleUpgrade - Callback function to handle upgrading the user's plan.
 * @param priceId - The Stripe price ID for the selected plan.
 * @param subscriptionId - (Optional) The current subscription ID, if applicable.
 * @param stripeCustomerId - (Optional) The Stripe customer ID, if applicable.
 * @property onContinue - (Optional) Callback function invoked when the user continues with the selected plan.
 * @property onDowngradeToFree - (Optional) Callback function invoked when user selects the FREE lifetime plan to downgrade.
 */
interface PlanCardProps {
  plan: PricingTier;
  selectedFrequency: string;
  currentUserPlan?: Subscription | null;
  pricingData: PricingData;
  handleUpgrade: (priceId: string, subscriptionId?: string | null, stripeCustomerId?: string | null) => void;
  onContinue?: () => void;
  onDowngradeToFree?: (productId?: string) => void;
}

/**
 * Renders a card UI for a subscription plan, displaying its details, pricing, features, and an action button
 * for upgrading, downgrading, or selecting the plan. Handles logic for determining the user's current plan,
 * whether the selected plan is an upgrade or downgrade, and manages button states and actions accordingly.
 *
 * @param plan The plan object containing details about the subscription plan.
 * @param selectedFrequency The currently selected billing frequency ('month' or 'year').
 * @param currentUserPlan The current plan of the user, if any.
 * @param pricingData An object containing all available plan data for comparison.
 * @param handleUpgrade Callback to handle upgrading to a new plan.
 * @param onContinue Optional callback for continuing on the free plan.
 */
/**
 * Converts lifetime cap limits object into displayable string array.
 * For example: { max_active_videos: 20 } becomes ["20 Maximum Active Videos"]
 */
function formatLifetimeCapLimits(limits: LifetimeCapLimits, plan: PricingTier): string[] {
  const result: string[] = [];

  if (limits.max_active_videos !== undefined) {
    result.push(`${limits.max_active_videos} Maximum Active Videos`);
  }

  // Add video duration if available
  if (plan.videoDuration) {
    result.push(`${plan.videoDuration}-Minute Duration`);
  }

  // Add video quality if available
  if (plan.videoQuality) {
    result.push(`${plan.videoQuality}p Quality`);
  }

  // Add storage duration if available
  if (plan.storageDuration) {
    result.push(`${plan.storageDuration} Storage`);
  }

  return result;
}

const PlanCard: FC<PlanCardProps> = ({
  plan,
  selectedFrequency,
  currentUserPlan,
  handleUpgrade,
  onContinue,
  onDowngradeToFree,
}) => {
  const [isThisPlanFree, setIsThisPlanFree] = useState(false);

  // Detect lifetime plan (FREE plan type)
  const isLifetimePlan = plan.plan_type === 'lifetime_cap';

  // Convert limits to displayable array (handles both array and object formats).
  // When monthlyVideos === 0 (unlimited), replace any stale "N Monthly Videos"
  // entry so the card never shows a number for unlimited plans.
  const limitsArray = useMemo(() => {
    if (Array.isArray(plan.limits)) {
      if (plan.monthlyVideos === 0) {
        const filtered = plan.limits.filter((s) => !/^(?:\d+|unlimited)\s+Monthly Videos$/i.test(s));
        return ['Unlimited Monthly Videos', ...filtered];
      }
      return plan.limits;
    }
    if (plan.limits && typeof plan.limits === 'object') {
      return formatLifetimeCapLimits(plan.limits as LifetimeCapLimits, plan);
    }
    return [];
  }, [plan.limits, plan.monthlyVideos, plan.videoDuration, plan.videoQuality, plan.storageDuration]);

  useEffect(() => {
    // Initialize pricing cache and check if plans are free
    const checkPlans = async () => {
      await initializePricingCache();
      const thisPlanFree = await isFreePlan(plan.product_id);
      setIsThisPlanFree(thisPlanFree);
    };
    checkPlans();
  }, [plan.product_id, currentUserPlan?.product_id]);

  // Prioritize price_id matching over product_id to handle stale product_id data
  const isUserOnMonthly = Boolean(
    currentUserPlan?.price_id &&
    plan.prices?.month?.stripePriceId &&
    currentUserPlan.price_id === plan.prices.month.stripePriceId,
  );
  const isUserOnAnnual = Boolean(
    currentUserPlan?.price_id &&
    plan.prices?.year?.stripePriceId &&
    currentUserPlan.price_id === plan.prices.year.stripePriceId,
  );

  // Only use product_id matching if no price_id is available (true free plan)
  const matchesByProduct =
    !currentUserPlan?.price_id && currentUserPlan?.product_id && plan.product_id === currentUserPlan.product_id;

  // A monthly subscriber viewing this tier's annual price can switch to annual billing,
  // so the annual card must not be locked as their current plan
  const canSwitchToAnnual =
    isUserOnMonthly && !isUserOnAnnual && selectedFrequency === 'year' && Boolean(plan.prices?.year?.stripePriceId);

  const isCurrentPlan = (isUserOnMonthly || isUserOnAnnual || matchesByProduct) && !canSwitchToAnnual;

  const getButtonText = () => {
    if (isCurrentPlan) {
      // Special case for free plan in modal
      if (isThisPlanFree && onContinue) {
        return 'Continue on Free Plan';
      }
      return isUserOnAnnual ? 'Current Plan (Annual)' : 'Current Plan';
    }
    if (canSwitchToAnnual) return 'Switch to Annual';
    return 'Select Plan';
  };

  const handleButtonClick = () => {
    // Special handling for free plan with continue option
    if (isCurrentPlan && isThisPlanFree && onContinue) {
      onContinue();
      return;
    }

    if (isCurrentPlan) return; // Don't do anything for current plan

    // Handle FREE lifetime plan selection (triggers downgrade).
    // Pass THIS card's product_id so the user downgrades to the plan they
    // actually selected, not a generic/stale free plan.
    if (isLifetimePlan && onDowngradeToFree) {
      onDowngradeToFree(plan.product_id);
      return;
    }

    const priceId = plan.prices?.[selectedFrequency as 'month' | 'year']?.stripePriceId;

    // All plan selections go to checkout (handleUpgrade)
    if (priceId) {
      handleUpgrade(priceId, currentUserPlan?.id, currentUserPlan?.stripe_customer_id);
    }
  };
  // Calculate annual savings
  const annualSavings = useMemo(() => {
    if (isLifetimePlan) return null;
    const monthlyPrice = typeof plan.prices?.month?.value === 'number' ? plan.prices.month.value : 0;
    const yearlyPrice = typeof plan.prices?.year?.value === 'number' ? plan.prices.year.value : 0;
    if (monthlyPrice <= 0 || yearlyPrice <= 0) return null;
    const annualCostMonthly = monthlyPrice * 12;
    const savingsPercent = Math.round(((annualCostMonthly - yearlyPrice) / annualCostMonthly) * 100);
    const perMonthEquivalent = Math.round((yearlyPrice / 12) * 100) / 100;
    return savingsPercent > 0 ? { savingsPercent, perMonthEquivalent, monthlyPrice } : null;
  }, [plan.prices?.month?.value, plan.prices?.year?.value, isLifetimePlan]);

  const freq = selectedFrequency as 'month' | 'year';

  return (
    <PlanWrapper>
      <Typography variant="h5" fontWeight="700" mt="8px" mb="8px">
        {plan.name}
      </Typography>
      <Typography variant="body2">{plan.subtext}</Typography>
      {plan.default_seats ? (
        <Typography variant="caption" fontWeight="600" color="primary.main" mt="2px">
          {plan.default_seats} {plan.default_seats === 1 ? 'Seat' : 'Seats'}
        </Typography>
      ) : null}
      <Stack direction="row" alignItems="baseline" spacing={0} mt="4px">
        <Typography variant="h5" fontWeight="700">
          {isLifetimePlan
            ? 'Free'
            : freq === 'year' && annualSavings
              ? `$${annualSavings.perMonthEquivalent.toFixed(2)}`
              : typeof plan.prices?.[freq]?.value === 'string'
                ? plan.prices?.[freq]?.value
                : `$${plan.prices?.[freq]?.value ?? 0}`}
        </Typography>
        {!isLifetimePlan && (
          <Typography variant="body2" color="text.secondary">
            {freq === 'year' && !annualSavings ? '/yr' : '/mo'}
          </Typography>
        )}
      </Stack>
      <Stack direction="column" alignItems="flex-start" spacing={0.25}>
        {isLifetimePlan && (
          <Typography variant="body2" letterSpacing="0.4px">
            Lifetime access
          </Typography>
        )}
        {freq === 'year' && !isLifetimePlan && annualSavings && (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
              ${annualSavings.monthlyPrice}/mo
            </Typography>
            <Typography variant="caption" fontWeight="600">
              billed annually
            </Typography>
          </Stack>
        )}
      </Stack>

      <Divider sx={{ marginTop: '12px', marginBottom: '4px' }} />

      {/* Handle both array (rate-based plans) and object (lifetime plans) for limits */}
      {limitsArray.length > 0 && <PlanList title="Workspace Limits" items={limitsArray} />}
      {Array.isArray(plan.featureList) && <PlanList title="Features" items={plan.featureList} />}
      {currentUserPlan?.scheduled_to_end && isThisPlanFree ? (
        <Typography variant="body2" color="textSecondary">
          Your subscription is scheduled to downgrade to Free at the end of the billing period.
        </Typography>
      ) : currentUserPlan?.scheduled_to_downgrade === plan.product_id && currentUserPlan?.scheduled_to_end !== true ? (
        <Typography variant="body2" color="textSecondary">
          Your subscription is scheduled to downgrade to {plan.name} at the end of the billing period.
        </Typography>
      ) : (
        <Button
          variant="contained"
          color="primary"
          onClick={handleButtonClick}
          disabled={Boolean(
            (isCurrentPlan && !(isThisPlanFree && onContinue)) ||
            currentUserPlan?.scheduled_to_downgrade === plan.product_id,
          )}
        >
          {getButtonText()}
        </Button>
      )}
    </PlanWrapper>
  );
};

export default PlanCard;
